import * as React from 'react';
import {FunctionComponent} from 'react';
import {VictoryAxis, VictoryChart, VictoryLine, VictoryScatter, VictoryTooltip} from 'victory';
import createTrend from 'trendline';

interface OwnProps {
    values: Array<{x: number; y: number}>;
    domainValues: Array<{x: number; y: number}>;
    formatX: (value: number) => string;
    formatY: (value: number) => string;
    showTrend?: boolean;
    highlights?: Array<{x: number; y: number}>;
}

const styles = {
    axis: {
        axis: {
            stroke: 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
            stroke: 'rgba(0, 0, 0, 0.1)',
            strokeWidth: 1,
            strokeDasharray: 4,
        },
        tickLabels: {
            fill: '#000',
            fontSize: 12,
            padding: 10,
        },
    },
};

export const Graph: FunctionComponent<OwnProps> = ({values, domainValues, formatX, formatY, showTrend, highlights}) => {
    const trend = createTrend(values, 'x', 'y');

    const trendStartX = Math.min(...domainValues.map((value) => value.x));
    const trendStartY = trend.calcY(trendStartX);
    const trendEndX = Math.max(...domainValues.map((value) => value.x));
    const trendEndY = trend.calcY(trendEndX);

    const yMin = Math.min(...domainValues.map((value) => value.y));
    const yMax = Math.max(...domainValues.map((value) => value.y));
    const yMargin = (yMax - yMin) * 0.1;

    const xMin = Math.min(...domainValues.map((value) => value.x));
    const xMax = Math.max(...domainValues.map((value) => value.x));
    const xMargin = (xMax - xMin) * 0.1;

    return (
        <VictoryChart
            style={{
                parent: {
                    minWidth: 300,
                    maxWidth: 400,
                    minHeight: 200,
                },
            }}
            padding={{top: 0, right: 0, bottom: 24, left: 48}}
            domain={{
                y: [Math.max(0, yMin - yMargin), yMax + yMargin],
                x: [Math.max(0, xMin - xMargin), xMax + xMargin],
            }}
        >
            {showTrend && (
                <VictoryLine
                    key={'trend'}
                    data={[
                        {x: trendStartX, y: trendStartY},
                        {x: trendEndX, y: trendEndY},
                    ]}
                    style={{
                        data: {
                            strokeDasharray: '4, 4',
                            opacity: 0.6,
                            strokeWidth: 1,
                        },
                    }}
                />
            )}
            <VictoryScatter
                key={'scatter'}
                data={values}
                size={3}
                labelComponent={
                    <VictoryTooltip
                        cornerRadius={4}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: 4,
                            borderColor: '#ccc',
                            borderWidth: 1,
                            fontSize: 8,
                            fontWeight: 400,
                        }}
                    />
                }
            />

            {highlights && highlights.length > 0 && (
                <VictoryScatter
                    key={'highlights'}
                    data={highlights}
                    size={7}
                    style={{
                        data: {
                            fill: '#3ac4e7',
                        },
                    }}
                />
            )}

            <VictoryAxis fixLabelOverlap style={styles.axis} tickFormat={(value: any) => formatX(value)} />
            <VictoryAxis
                style={styles.axis}
                dependentAxis
                fixLabelOverlap
                orientation="left"
                tickFormat={(value: any) => formatY(value)}
            />
        </VictoryChart>
    );
};
